import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errText } from "../lib/api";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Reusable Giphy-powered GIF search picker. Calls backend proxy (/giphy/search)
// so the API key stays server-side. onSelect receives the chosen GIF url.
export function GifPicker({ open, onClose, onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef();

  const load = async (query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/giphy/search", { params: { q: query, limit: 24 } });
      setResults(data.results || []);
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setQ(""); setResults([]); load("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(q), 350);
    return () => clearTimeout(debounce.current);
  }, [q]); // eslint-disable-line

  const pick = (url) => { onSelect(url); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-testid="gif-picker">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-base">Search GIFs
            <span className="text-[10px] font-normal text-muted-foreground">via GIPHY</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Search and pick a GIF</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 surface-2 border border-white/10 rounded-xl px-3 shrink-0">
          <Search size={16} className="text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Giphy…"
            className="bg-transparent flex-1 py-2.5 text-sm outline-none" data-testid="gif-search-input" />
        </div>
        <div className="overflow-y-auto mt-1 -mx-1 px-1" data-testid="gif-results">
          {loading && results.length === 0 ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-eblue" /></div>
          ) : results.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-12">No GIFs found</div>
          ) : (
            <div className="columns-2 sm:columns-3 gap-2">
              {results.map((g) => (
                <button key={g.id} type="button" onClick={() => pick(g.url)} data-testid={`gif-item-${g.id}`}
                  className="mb-2 w-full overflow-hidden rounded-lg border border-white/5 hover:border-eblue/60 transition-colors block">
                  <img src={g.preview} alt={g.title} loading="lazy" className="w-full block" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
